import GetTogglesRequest from '../Toggles/GetTogglesRequest'
import { API_COMPOSER_SERVICE } from '@eggplant/api-hosts'
import {
  AuthenticatedDataRequest,
  AuthenticatedPolling
} from '@eggplant/data-request'
import {
  InvisiButton,
  Paragraph,
  PollingIndicator,
  ResultsCount,
  ResultsFooter,
  Timezone,
  Tooltip
} from '@eggplant/pattern-library'
import { useGetParamsWithSpaceId } from '../useGetParamsWithSpaceId'
import { useState } from 'react'
import type {
  Error,
  GetCollectionResponseBase,
  TogglesResponse
} from '@eggplant/types'

interface Props<T> {
  isPolling: boolean
  onError?: (error: Error) => void
  onSuccess: (response: T) => void
  onPollingToggle: () => void
  params: Record<string, string | number | undefined>
}

const style = {
  display: 'grid',
  gap: '12px',
  gridAutoFlow: 'column',
  alignItems: 'start'
}

// Abstracts the switch between a single request and polling

const PollTaskToggle = <T extends GetCollectionResponseBase, >({
  isPolling,
  onError,
  onSuccess,
  onPollingToggle,
  params
}: Props<T>) => {
  const [togglePolling, setTogglePolling] = useState(isPolling)
  const [intervalMs, setIntervalMs] = useState(7000)

  const url = `${API_COMPOSER_SERVICE}/runner/test_configurations`

  const handleDataRequestSuccess = (data: TogglesResponse) => {
    const intervalSeconds = data.deployment_flags.RUNNER_REFRESH_INTERVAL * 1000

    if (intervalSeconds > 0) {
      setIntervalMs(intervalSeconds)
      setTogglePolling(true)
    } else {
      setTogglePolling(false)
    }
  }

  const buttonContent = (
    <PollingIndicator
      hasLabel
      isPolling={isPolling}
    />
  )

  const title = isPolling
    ? `Updating results every ${intervalMs / 1000} seconds. Click to stop.`
    : 'Refresh results and enable automatic updates.'

  const children = ({ total_count }: T) =>
    <ResultsFooter>
      <Paragraph>
        <span style={style}>
          <ResultsCount value={total_count} />
          {
            togglePolling &&
              <Tooltip label={title}>
                <InvisiButton
                  hasBackgroundColor={false}
                  hasPadding={false}
                  label={title}
                  onClick={onPollingToggle}
                >
                  {buttonContent}
                </InvisiButton>
              </Tooltip>
          }
        </span>
      </Paragraph>
      <Timezone />
    </ResultsFooter>

  const { requestParams } = useGetParamsWithSpaceId(params)

  const props = {
    onError,
    onSuccess,
    params: requestParams,
    url,
    children
  }

  return isPolling && togglePolling
    ? (
      <GetTogglesRequest onSuccess={handleDataRequestSuccess}>
        {
          () =>
            <AuthenticatedPolling<T>
              intervalMs={intervalMs}
              isPolling
              key={JSON.stringify(params)} // refreshes the component to restart polling
              {...props}
            />
        }
      </GetTogglesRequest>
    )
    : (
      <AuthenticatedDataRequest
        method='get'
        {...props}
      />
    )
}

export default PollTaskToggle
